import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";

export default function MessagesView() {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [confirm, ConfirmModal] = useConfirm();

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get("/contact");
      setMsgs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh();
  }, []);

  async function setStatus(id, status) {
    try {
      await api.patch(`/contact/${id}`, { status });
      setMsgs((cur) => cur.map((m) => (m.id === id ? { ...m, status } : m)));
      if (active?.id === id) setActive({ ...active, status });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function remove(id) {
    if (!(await confirm("Delete this message?"))) return;
    try {
      await api.delete(`/contact/${id}`);
      setMsgs((cur) => cur.filter((m) => m.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="messages-view">
      <ConfirmModal />
      {loading ? (
        <div className="text-neutral-400">Loading…</div>
      ) : msgs.length === 0 ? (
        <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No messages yet.</div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-2 space-y-3">
            {msgs.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  setActive(m);
                  if (m.status === "new") setStatus(m.id, "read");
                }}
                data-testid={`msg-row-${m.id}`}
                className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                  active?.id === m.id ? "border-axe-purple/60 bg-axe-surface" : "border-axe-border bg-axe-surface/70 hover:border-axe-purple/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display font-semibold text-white truncate">{m.name}</div>
                  <span className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full ${
                    m.status === "new" ? "bg-axe-tangerine text-black"
                    : m.status === "replied" ? "bg-axe-purple text-white"
                    : "bg-axe-bg text-neutral-400 border border-axe-border"
                  }`}>{m.status}</span>
                </div>
                <div className="text-xs text-neutral-500 truncate mt-1">{m.email}</div>
                {m.subject && <div className="text-xs text-neutral-400 mt-1 truncate">{m.subject}</div>}
                <div className="text-[10px] text-neutral-600 mt-2">{new Date(m.created_at).toLocaleString()}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3">
            {active ? (
              <div className="rounded-2xl border border-axe-border bg-axe-surface p-6" data-testid="msg-detail">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-axe-tangerine font-bold">{active.status}</div>
                    <h3 className="mt-2 font-display font-black text-2xl">{active.subject || "(no subject)"}</h3>
                    <div className="mt-2 text-sm text-neutral-400">
                      From <span className="text-white">{active.name}</span> · {active.email}
                    </div>
                    <div className="text-[11px] text-neutral-600 mt-1">{new Date(active.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="mt-5 whitespace-pre-line text-neutral-200 leading-relaxed border-t border-axe-border pt-5">
                  {active.message}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={`mailto:${active.email}?subject=${encodeURIComponent("Re: " + (active.subject || "Your message"))}`}
                    onClick={() => setStatus(active.id, "replied")}
                    className="inline-flex items-center gap-2 rounded-full bg-axe-purple px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover"
                    data-testid="msg-reply"
                  >
                    <Mail size={14} /> Reply by email
                  </a>
                  <button onClick={() => setStatus(active.id, active.status === "replied" ? "new" : "replied")} className="rounded-full border border-axe-border px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-bg" data-testid="msg-toggle-replied">
                    Mark {active.status === "replied" ? "new" : "replied"}
                  </button>
                  <button onClick={() => remove(active.id)} className="inline-flex items-center gap-1 rounded-full border border-red-500/40 text-red-300 px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-red-500/10" data-testid="msg-delete">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-axe-border bg-axe-surface p-8 text-neutral-500 text-center">
                Select a message to view.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
